import { Stipulation } from "@/models/opal";
import { ComputedRef, Ref, computed, ref } from "vue";
import { useStore } from "vuex";
import {
  StipulationStatusIds,
  CreditStipulationTypeIds,
  PayoffVerificationStipulationTypeIds,
  IncomeStipulationTypeIds,
} from "@/config/constants";
import useChecklistHelpers from "@/use/checklistHelpers";
import { Applicant } from "@/models/application";

export const isStipulationType = (stipulation: Stipulation, stipulationTypeId: number) => {
  return stipulation.stipulationTypeId === stipulationTypeId;
};

export const isStipulationStatus = (stipulation: Stipulation, stipulationStatusId: number) => {
  return stipulation.stipulationStatusId === stipulationStatusId;
};

export function useStipulation(stipulation: ComputedRef<Stipulation>) {
  const store = useStore();

  const { abstractTfn } = useChecklistHelpers();

  const lenderData = computed(() => store.state.lenderData);
  const configuration = computed(() => store.state.configuration);

  const stipulationText = computed(() => stipulation.value.stipulationText);

  const isComplete = computed<boolean>(() => {
    const stipTypeIds = [StipulationStatusIds.COMPLETED, StipulationStatusIds.CU_OVERRIDE];
    return stipTypeIds.includes(stipulation.value.stipulationStatusId);
  });

  const isPending = computed(() => {
    const stipTypeIds = [StipulationStatusIds.PENDING];
    return stipTypeIds.includes(stipulation.value.stipulationStatusId);
  });

  const isCreditBureauAction = computed<boolean>(() => {
    const stipTypeIds = [
      CreditStipulationTypeIds.UnSuppressBureau,
      CreditStipulationTypeIds.UnfreezeCredit,
      CreditStipulationTypeIds.ContactCBRCorrection,
    ];

    return stipTypeIds.includes(stipulation.value.stipulationTypeId);
  });

  const tollFreeNumber = computed<string>(() => {
    return isCreditBureauAction.value
      ? abstractTfn(stipulationText.value)
      : store.state.lenderData && store.state.lenderData.tollFreeNumber
      ? store.state.lenderData.tollFreeNumber
      : "N/A";
  });

  const hrefTFN = computed<string>(() => {
    return isCreditBureauAction.value
      ? `+${tollFreeNumber.value.replace(/-()/g, "")}`
      : store.state.lenderData && store.state.lenderData.tollFreeNumber
      ? `+1${store.state.lenderData.tollFreeNumber.replace(/-()/g, "")}`
      : "N/A";
  });

  const emailAddress = computed<string>(() => {
    return store.state.lenderData && store.state.lenderData.supportEmailAddress
      ? store.state.lenderData.supportEmailAddress
      : "member_support@studentchoice.org";
  });

  const showSampleDocument = computed<boolean>(() => {
    const stipTypeIds = [
      PayoffVerificationStipulationTypeIds.LoanAccountStatement,
      PayoffVerificationStipulationTypeIds.PayoffStatement,
    ];
    return stipTypeIds.includes(stipulation.value.stipulationTypeId);
  });

  const sampleDocumentCdnUrl = computed(() => {
    switch (stipulation.value.stipulationTypeId) {
      case PayoffVerificationStipulationTypeIds.PayoffStatement:
        return store.state.configuration?.payOffSample;
      case PayoffVerificationStipulationTypeIds.LoanAccountStatement:
        return store.state.configuration?.loanAccountSample;
      default:
        return undefined;
    }
  });

  // Category assigned to stips that do not display stip description
  const hideStipTextStips = ref([IncomeStipulationTypeIds.IncomeVerification]);

  const htmlStipTextStipsWithMergeFields = ref([
    IncomeStipulationTypeIds.NonIncomeResponsibleApplicant,
  ]);

  const htmlStipTextStips = ref([IncomeStipulationTypeIds.AutomatedIncomeVerificationConsent]);

  const assignedApplicant = computed<Applicant>(() =>
    store.state.appData.applicants.find((a: Applicant) => a.id === stipulation.value.applicantId),
  );

  const isApplicantStip = computed<boolean>(
    () => assignedApplicant.value && assignedApplicant.value?.id > 0,
  );

  return {
    isCreditBureauAction,
    tollFreeNumber,
    hrefTFN,
    emailAddress,
    stipulationText,
    showSampleDocument,
    sampleDocumentCdnUrl,
    hideStipTextStips,
    htmlStipTextStipsWithMergeFields,
    htmlStipTextStips,
    lenderData,
    configuration,
    isPending,
    isComplete,
    assignedApplicant,
		isApplicantStip
  };
}
